import {
  DropdownField,
  TextField,
  TanstackFieldWrapper,
} from 'components/form-fields-2';
import {
  Layer,
  ComboBox,
  FilterableMultiSelect,
  InlineLoading,
  InlineNotification,
  Loading,
  Tooltip,
} from '@carbon/react';
import { Information } from '@carbon/react/icons';
import { LaunchLink } from 'components/launch-link';
import { useParams } from 'react-router-dom';
import styles from './area-configs.module.scss';
import sectionStyles from './config-sections.module.scss';
import { useScenarioForm } from './contexts';
import { capitalize, debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  intersectShapesAtom,
  createAreaGeometryCategoriesAtom,
  createAreaGeometryDetailsSearchAtom,
  AreaGeometryResult,
  fetchOperatorsAtom,
  fetchSubAreaDetailAtom,
  validateAreaConfigAtom,
  validateAreaConfigBaseAtom,
} from './atoms';
import {
  AreaConfig,
  AreaConfigResponse,
  BasinAreaCategory,
  DevelopmentType,
  SubArea,
  SubAreaCategory,
  SubAreaResponse,
  StructureGridCollectionDetail,
} from './types';
import classNames from 'classnames';
import { createResourceAtom } from 'lib/atoms';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { AreaConfigsSkeleton } from './section-skeletons';

const FALLBACK_ATOM = atom(null);

const SUB_AREA_CATEGORIES: SubAreaCategory[] = ['type_curve_area', 'unit'];

const DEVELOPMENT_TYPES: DevelopmentType[] = ['Horizontal', 'Vertical', 'All'];

const categoryLabel = (cat: string | null) => {
  if (!cat) return '';
  return cat
    .split('_')
    .map((word) => capitalize(word))
    .join(' ');
};

const subAreaCategoryToTargetModel = (cat: SubAreaCategory): string => {
  switch (cat) {
    case 'type_curve_area':
      return 'TypeCurveArea';
    case 'unit':
      return 'DevelopmentArea';
  }
};

const developmentTypeToHoleDirectionIds = (
  devType: DevelopmentType | null
): string[] => {
  switch (devType) {
    case 'Horizontal':
      return ['HORIZONTAL'];
    case 'Vertical':
      return ['VERTICAL'];
    case 'All':
      return ['HORIZONTAL', 'VERTICAL'];
    default:
      return ['HORIZONTAL'];
  }
};

interface AreaGeometryDetailResult extends AreaGeometryResult {
  geometry: object | null;
}

const capitalizeDevelopmentType = (
  value: string | null
): DevelopmentType | null => {
  if (!value) return null;
  const lower = value.toLowerCase();
  if (lower === 'horizontal') return 'Horizontal';
  if (lower === 'vertical') return 'Vertical';
  if (lower === 'all') return 'All';
  return value as DevelopmentType;
};

const mapSubAreaResponses = (subAreas: SubAreaResponse[]): SubArea[] =>
  subAreas.map((sa) => ({
    id: String(sa.typeCurveArea?.id || sa.developmentArea?.id || sa.id),
    name: sa.typeCurveArea?.name || sa.developmentArea?.name || '',
    href: sa.href,
    associationId: String(sa.id),
  }));

const mapDetailToAreaConfig = (detail: AreaConfigResponse): AreaConfig => ({
  basinAreaCategory: detail.structureGridCollection
    ? 'structure_grid_collection'
    : detail.areaGeometry
      ? 'area_geometry'
      : null,
  areaGeometryId: detail.areaGeometry ? String(detail.areaGeometry.id) : null,
  areaGeometryName: detail.areaGeometry?.name ?? null,
  structureGridCollectionId: detail.structureGridCollection
    ? String(detail.structureGridCollection.id)
    : null,
  structureGridCollectionName:
    detail.structureGridCollection?.collectionName ?? null,
  basinName: detail.basinName,
  subAreaCategory: detail.subAreaCategory,
  operatorNames: detail.operatorNames || [],
  subAreas: mapSubAreaResponses(detail.subAreas || []),
  developmentType:
    capitalizeDevelopmentType(detail.developmentType) ?? 'Horizontal',
  algorithmicFormationPriority: detail.algorithmicFormationPriority,
});

export const AreaConfigs = () => {
  const { id: scenarioId } = useParams<{ id: string }>();
  const {
    form,
    baselines,
    selectedAreaConfig,
    setAreaConfigs,
    setFieldBaseline,
    readOnly,
    setSubAreaGeometries,
    setFocusedSubAreaId,
    setStructureGridCollectionData,
    validConfigState,
    setValidConfigState,
    setIsValidating,
    setFormationsTrigger,
    setManualFormationPriority,
  } = useScenarioForm();

  // Track which slot's detail has been hydrated into the form. Keyed on the
  // UI-stable elementId — see effect below for why.
  const initializedForConfigRef = useRef<string | null>(null);

  // This effect fires on a *config switch*, not on save. We key on
  // `elementId` (a stable UI identifier assigned at create-time / on initial
  // server load) instead of the server `id`, because the save flow flips
  // id from 'local' to a server id but keeps elementId the same — so a save
  // is invisible here and the user's just-saved local state isn't wiped.
  useEffect(() => {
    setSelectedCategory(null);
    setSelectedAreaGeometry(null);
    lastSelectedAreaNameRef.current = null;
    setSubAreaOptions([]);
    setOperatorOptions([]);
    setSubAreaGeometries({});
    setStructureGridCollectionData(null);
    initializedForConfigRef.current = null;
    areaInitializedForConfigRef.current = null;

    if (!selectedAreaConfig) return;
    if (selectedAreaConfig.href) return; // Saved config — wait for detail fetch

    const areaConfig: AreaConfig = {
      basinAreaCategory: null,
      areaGeometryId: null,
      areaGeometryName: null,
      structureGridCollectionId: null,
      structureGridCollectionName: null,
      basinName: null,
      subAreaCategory: null,
      operatorNames: [],
      subAreas: [],
      developmentType: 'Horizontal',
      algorithmicFormationPriority: true,
    };

    form.setFieldValue('areaConfig', areaConfig);
    setFieldBaseline('areaConfig', areaConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAreaConfig?.elementId]);

  // For saved configs, fetch the full detail from the API
  const areaConfigDetailAtom = useMemo(() => {
    if (!selectedAreaConfig?.href) return FALLBACK_ATOM;
    return createResourceAtom<AreaConfigResponse>(selectedAreaConfig.href);
  }, [selectedAreaConfig?.href]);
  const areaConfigDetail = useAtomValue(areaConfigDetailAtom);

  // When detail loads, populate form and sub-area geometries
  useEffect(() => {
    const elementId = selectedAreaConfig?.elementId ?? null;
    if (initializedForConfigRef.current === elementId) return;
    if (!areaConfigDetail?.data || 'error' in areaConfigDetail.data) return;
    initializedForConfigRef.current = elementId;

    const detail = areaConfigDetail.data;
    const areaConfig = mapDetailToAreaConfig(detail);
    form.setFieldValue('areaConfig', areaConfig);
    setFieldBaseline('areaConfig', areaConfig);

    // Initialize the manual formation priority toggle from the saved value
    setManualFormationPriority(!areaConfig.algorithmicFormationPriority);

    // Update the area configs list with full detail response. Preserve the
    // UI-stable elementId from the existing slot so the switch effect
    // doesn't see a "new" config and reset.
    setAreaConfigs((prev) =>
      prev.map((c) => {
        if (String(c.id) !== String(detail.id)) return c;
        return { ...detail, elementId: c.elementId };
      })
    );

    // Load sub-area resolved geometries for map (keyed by area id)
    const geometries: Record<string, any> = {};
    (detail.subAreas || []).forEach((sa) => {
      if (sa.resolvedGeometry) {
        const areaId = String(
          sa.typeCurveArea?.id || sa.developmentArea?.id || sa.id
        );
        geometries[areaId] = sa.resolvedGeometry;
      }
    });
    setSubAreaGeometries(geometries);

    // Fetch sub-area options if the saved config has a sub-area category
    if (detail.subAreaCategory && detail.areaGeometry) {
      const savedSubAreas = mapSubAreaResponses(detail.subAreas || []);
      const areaGeometryId = String(detail.areaGeometry.id);

      runIntersectShapes({
        payload: {
          source_geometries: [
            { model_type: 'AreaGeometry', model_id: Number(areaGeometryId) },
          ],
          target_models: [subAreaCategoryToTargetModel(detail.subAreaCategory)],
          coverage_threshold: 0,
        },
        onSuccess: (data) => {
          const group = data[0];
          const intersectSubAreas: SubArea[] = group
            ? group.results.map((r) => {
                const resource =
                  r.resources?.developmentArea || r.resources?.typeCurveArea;
                return {
                  id: String(r.targetId),
                  name: r.targetName,
                  href: resource?.href ?? '',
                };
              })
            : [];

          // Merge saved sub-areas that aren't in the intersect results
          const intersectIds = new Set(intersectSubAreas.map((s) => s.id));
          const missingFromIntersect = savedSubAreas.filter(
            (s) => !intersectIds.has(s.id)
          );

          setSubAreaOptions([...intersectSubAreas, ...missingFromIntersect]);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaConfigDetail?.data]);

  const runIntersectShapes = useSetAtom(intersectShapesAtom);
  const runFetchOperators = useSetAtom(fetchOperatorsAtom);
  const runFetchSubAreaDetail = useSetAtom(fetchSubAreaDetailAtom);
  const runValidateAreaConfig = useSetAtom(validateAreaConfigAtom);
  const validateState = useAtomValue(validateAreaConfigBaseAtom);

  // Loading states for label spinners + disabling dropdowns while data is
  // being fetched for selection (text search loading is tracked separately
  // and excluded so typing doesn't repeatedly disable the combobox).
  const intersectShapesState = useAtomValue(intersectShapesAtom);
  const fetchOperatorsState = useAtomValue(fetchOperatorsAtom);

  const [isTextSearching, setIsTextSearching] = useState(false);

  const [subAreaOptions, setSubAreaOptions] = useState<SubArea[]>([]);
  const [operatorOptions, setOperatorOptions] = useState<string[]>([]);

  // Area geometry search atoms
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedAreaGeometry, setSelectedAreaGeometry] =
    useState<AreaGeometryResult | null>(null);
  const [categories, setCategories] = useState<string[]>([]);

  const categoriesAtoms = useMemo(() => createAreaGeometryCategoriesAtom(), []);
  const areaSearchAtoms = useMemo(
    () => createAreaGeometryDetailsSearchAtom(),
    []
  );

  const [categoriesAtomValue] = useAtom(categoriesAtoms.searchAtom);
  const [areaSearchValue, setAreaSearchPayload] = useAtom(
    areaSearchAtoms.searchAtom
  );

  const previousFacetsRef = useRef<string>('');

  // Tracks the basin area name that was just written into the ComboBox's
  // input either by a user selection or by a programmatic setSelectedAreaGeometry
  // (e.g. saved-config hydrate). Carbon's ComboBox fires onInputChange
  // synchronously whenever the input value changes — including when it
  // mirrors the selected item back into the input — so without this guard
  // every selection would dispatch a redundant text search whose `text`
  // equals the item's own name. Reading from selectedAreaGeometry would
  // see the stale (pre-selection) value because Carbon fires
  // onInputChange in the same React tick as onChange.
  const lastSelectedAreaNameRef = useRef<string | null>(null);

  // Fetch area geometry detail (with geoJson) when an area is selected
  const areaGeometryDetailAtom = useMemo(() => {
    if (!selectedAreaGeometry?.href) return FALLBACK_ATOM;
    return createResourceAtom<AreaGeometryDetailResult>(
      selectedAreaGeometry.href
    );
  }, [selectedAreaGeometry?.href]);
  const areaGeometryDetail = useAtomValue(areaGeometryDetailAtom);

  // Fetch structure grid collection when area geometry has one
  const structureGridCollectionHref = useMemo(() => {
    const detail = areaGeometryDetail?.data;
    if (!detail || 'error' in detail) return null;
    return detail.resources?.structureGridCollections?.href ?? null;
  }, [areaGeometryDetail?.data]);

  const structureGridCollectionAtom = useMemo(() => {
    if (!structureGridCollectionHref) return FALLBACK_ATOM;
    return createResourceAtom<StructureGridCollectionDetail>(
      structureGridCollectionHref
    );
  }, [structureGridCollectionHref]);
  const structureGridCollectionValue = useAtomValue(
    structureGridCollectionAtom
  );

  // When structure grid collection loads, update basin name and share data.
  // Only update form fields for new configs or when the user changed the area geometry
  // (not for saved configs being loaded — their values come from the detail fetch).
  useEffect(() => {
    if (
      !structureGridCollectionValue?.data ||
      'error' in structureGridCollectionValue.data
    )
      return;

    const collection = structureGridCollectionValue.data;
    setStructureGridCollectionData(collection);

    // Skip form updates if the detail fetch already set these values
    if (initializedForConfigRef.current) {
      const currentBasinName = form.state.values.areaConfig?.basinName;
      if (currentBasinName === collection.basin) return;
    }

    const updatedAreaConfig = {
      ...form.state.values.areaConfig!,
      basinName: collection.basin,
      structureGridCollectionName: collection.collectionName,
      structureGridCollectionId: String(collection.id),
    };
    form.setFieldValue('areaConfig', updatedAreaConfig);

    // Only update baseline for saved configs (detail already loaded).
    // For new configs, keep the initial empty baseline so isModified stays true.
    if (initializedForConfigRef.current) {
      setFieldBaseline('areaConfig', updatedAreaConfig);
    }
  }, [
    structureGridCollectionValue?.data,
    form,
    setStructureGridCollectionData,
    setFieldBaseline,
  ]);

  // Initialize category/area selection from detail response's area geometry
  const areaInitializedForConfigRef = useRef<string | null>(null);
  useEffect(() => {
    const elementId = selectedAreaConfig?.elementId ?? null;
    if (areaInitializedForConfigRef.current === elementId) return;
    if (!areaConfigDetail?.data || 'error' in areaConfigDetail.data) return;

    const detail = areaConfigDetail.data;
    if (!detail.areaGeometry) {
      areaInitializedForConfigRef.current = elementId;
      return;
    }

    areaInitializedForConfigRef.current = elementId;
    const areaGeom = detail.areaGeometry;

    // Suppress the onInputChange Carbon will fire when it mirrors this
    // programmatic selection back into the ComboBox input.
    lastSelectedAreaNameRef.current = areaGeom.name;

    // Use the area geometry to set the category filter and selected area
    setSelectedAreaGeometry({
      id: Number(areaGeom.id),
      name: areaGeom.name,
      href: areaGeom.href,
      category: '', // Will be resolved when area geometry detail loads
    } as AreaGeometryResult);

    setAreaSearchPayload((current) => ({
      ...current,
      page: 1,
      pageSize: 200,
    }));
  }, [
    areaConfigDetail?.data,
    setAreaSearchPayload,
    selectedAreaConfig?.elementId,
  ]);

  // Extract categories from facets
  useEffect(() => {
    if (!categoriesAtomValue.data?.facets) return;

    const facetsHash = JSON.stringify(categoriesAtomValue.data.facets);
    if (facetsHash === previousFacetsRef.current) return;
    previousFacetsRef.current = facetsHash;

    const facets = categoriesAtomValue.data.facets as Record<string, any>;
    const categoryFacet = facets?.category;
    if (categoryFacet?.buckets) {
      const categoryValues = categoryFacet.buckets
        .map((b: { value: string }) => b.value)
        .filter((v: string) => v && v.trim() !== '')
        .sort();
      setCategories(categoryValues);
    }
  }, [categoriesAtomValue.data?.facets]);

  // When area geometry detail loads, update the area config in context for the map
  const selectedAreaConfigIdRef = useRef(selectedAreaConfig?.id);
  selectedAreaConfigIdRef.current = selectedAreaConfig?.id;

  useEffect(() => {
    if (!areaGeometryDetail?.data || 'error' in areaGeometryDetail.data) return;
    const configId = selectedAreaConfigIdRef.current;
    if (!configId) return;

    const detail = areaGeometryDetail.data;

    // Set category from area geometry detail (for existing configs loaded via detail fetch)
    if (detail.category && !selectedCategory) {
      setSelectedCategory(detail.category);
      setAreaSearchPayload((current) => ({
        ...current,
        category:
          detail.category && detail.category !== 'All'
            ? detail.category
            : undefined,
        page: 1,
        pageSize: 200,
      }));
    }

    // Update only the geoJson on the config for the map rendering.
    // Preserve the existing areaGeometry name so the area panel title
    // only reflects the saved (database) value, not the in-progress selection.
    setAreaConfigs((prev) =>
      prev.map((config) => {
        if (config.id !== configId) return config;
        return {
          ...config,
          areaGeometry: {
            ...(config.areaGeometry || {
              id: '',
              name: '',
              href: '',
              auth: [],
              geoJson: null,
            }),
            geoJson: detail.geometry ?? null,
          },
        };
      })
    );
  }, [
    areaGeometryDetail?.data,
    setAreaConfigs,
    selectedCategory,
    setAreaSearchPayload,
  ]);

  // Fetch operators when area geometry is selected or development type changes
  const handleFetchOperators = useCallback(
    (geometry: object, developmentType: DevelopmentType | null) => {
      if (!geometry) return;

      runFetchOperators({
        payload: {
          query: {
            page: 1,
            pageSize: 1,
            useElastic: true,
            holeDirectionIds:
              developmentTypeToHoleDirectionIds(developmentType),
            polygonGeojson: geometry,
          },
        },
        onSuccess: (operators) => {
          setOperatorOptions(operators);
        },
      });
    },
    [runFetchOperators]
  );

  // Trigger operator fetch when area geometry detail loads
  const operatorsFetchedForRef = useRef<string>('');
  useEffect(() => {
    if (!areaGeometryDetail?.data || 'error' in areaGeometryDetail.data) return;
    const geometry = areaGeometryDetail.data.geometry;
    if (!geometry) return;

    const devType = form.state.values.areaConfig?.developmentType ?? null;
    const fetchKey = `${areaGeometryDetail.data.id}-${devType}`;
    if (operatorsFetchedForRef.current === fetchKey) return;
    operatorsFetchedForRef.current = fetchKey;

    handleFetchOperators(geometry, devType);
  }, [
    areaGeometryDetail?.data,
    form.state.values.areaConfig?.developmentType,
    handleFetchOperators,
  ]);

  // Build area geometry options list
  const areaGeometries: AreaGeometryResult[] = useMemo(() => {
    const searchResults = areaSearchValue.data?.results ?? [];

    if (!selectedCategory) {
      return selectedAreaGeometry ? [selectedAreaGeometry] : [];
    }

    const options = [...searchResults];
    if (
      selectedAreaGeometry &&
      !options.some((o) => o.id === selectedAreaGeometry.id)
    ) {
      options.push(selectedAreaGeometry);
    }
    return options;
  }, [areaSearchValue.data?.results, selectedAreaGeometry, selectedCategory]);

  const debouncedTextSearch = useMemo(
    () =>
      debounce((text: string) => {
        setIsTextSearching(true);
        setAreaSearchPayload((current) => ({
          ...current,
          text: text || undefined,
          page: 1,
        }));
      }, 350),
    [setAreaSearchPayload]
  );

  useEffect(() => {
    return () => {
      debouncedTextSearch.cancel();
    };
  }, [debouncedTextSearch]);

  // Clear the text-searching flag once the fetch settles. Any subsequent
  // load (category change, initial preselect) will then be treated as a
  // real "loading" event and show the label spinner.
  useEffect(() => {
    if (!areaSearchValue.loading && isTextSearching) {
      setIsTextSearching(false);
    }
  }, [areaSearchValue.loading, isTextSearching]);

  const basinAreaLoading = areaSearchValue.loading && !isTextSearching;

  const handleCategoryChange = useCallback(
    (value: string | null) => {
      setSelectedCategory(value);
      setSelectedAreaGeometry(null);
      lastSelectedAreaNameRef.current = null;

      // Cancel any pending text search so the old area name doesn't leak
      // into the new category's search request. Also clear the
      // text-searching flag so the spinner shows for this category-driven
      // fetch (not suppressed as if it were typing).
      debouncedTextSearch.cancel();
      setIsTextSearching(false);

      // Reset area config fields, structure grid, and formations on category change
      form.setFieldValue('areaConfig', {
        ...form.state.values.areaConfig!,
        areaGeometryId: null,
        areaGeometryName: null,
        basinName: null,
        structureGridCollectionId: null,
        structureGridCollectionName: null,
        subAreas: [],
      });
      setStructureGridCollectionData(null);
      setSubAreaOptions([]);
      setOperatorOptions([]);
      setSubAreaGeometries({});
      fetchedSubAreaIdsRef.current.clear();
      form.setFieldValue('formationMappings', []);

      setAreaSearchPayload((current) => ({
        ...current,
        text: undefined,
        category: value && value !== 'All' ? value : undefined,
        page: 1,
        pageSize: 200,
      }));
    },
    [
      setAreaSearchPayload,
      form,
      setStructureGridCollectionData,
      setSubAreaGeometries,
      debouncedTextSearch,
    ]
  );

  const categoryItems = useMemo(() => ['All', ...categories], [categories]);

  // Fetch sub-areas when area and sub-area category change
  const handleFetchSubAreas = useCallback(
    (category: SubAreaCategory, areaId?: string | null) => {
      if (!category || !areaId) return;

      runIntersectShapes({
        payload: {
          source_geometries: [
            { model_type: 'AreaGeometry', model_id: Number(areaId) },
          ],
          target_models: [subAreaCategoryToTargetModel(category)],
          coverage_threshold: 0,
        },
        onSuccess: (data) => {
          const group = data[0];
          if (!group) {
            setSubAreaOptions([]);
            return;
          }
          const subAreas: SubArea[] = group.results.map((r) => {
            const resource =
              r.resources?.developmentArea || r.resources?.typeCurveArea;
            return {
              id: String(r.targetId),
              name: r.targetName,
              href: resource?.href ?? '',
            };
          });
          setSubAreaOptions(subAreas);
        },
      });
    },
    [runIntersectShapes]
  );

  // Track which sub-area IDs we've already fetched or are fetching
  const fetchedSubAreaIdsRef = useRef<Set<string>>(new Set());

  // Fetch sub-area detail and store geometry for map
  const handleSubAreaSelection = useCallback(
    (selectedItems: SubArea[]) => {
      const selectedIds = new Set(selectedItems.map((i) => i.id));

      // Clean up deselected geometries and fetched tracking
      setSubAreaGeometries((prev) => {
        const prevIds = Object.keys(prev);
        const removedIds = prevIds.filter((id) => !selectedIds.has(id));
        if (removedIds.length === 0) return prev;

        const next: Record<string, any> = {};
        for (const id of prevIds) {
          if (selectedIds.has(id)) {
            next[id] = prev[id];
          } else {
            fetchedSubAreaIdsRef.current.delete(id);
          }
        }
        return next;
      });

      // Only fetch geometry for items we haven't fetched yet
      selectedItems.forEach((item) => {
        if (item.href && !fetchedSubAreaIdsRef.current.has(item.id)) {
          fetchedSubAreaIdsRef.current.add(item.id);
          runFetchSubAreaDetail({
            href: item.href,
            onSuccess: (data: any) => {
              if (data?.geometry) {
                setSubAreaGeometries((prev) => ({
                  ...prev,
                  [item.id]: data.geometry,
                }));
                setFocusedSubAreaId(item.id);
              }
            },
          });
        }
      });
    },
    [runFetchSubAreaDetail, setSubAreaGeometries, setFocusedSubAreaId]
  );

  // --- Area config validation ---
  // Sub-path deps below are intentional: depending on the full areaConfig
  // object would recompute on every unrelated field change (basinName, etc.)
  // and re-trigger validation requests.
  const validationKey = useMemo(() => {
    const ac = form.state.values.areaConfig;
    if (!ac?.areaGeometryId || !ac?.developmentType || !scenarioId) return null;
    return JSON.stringify({
      areaGeometryId: ac.areaGeometryId,
      developmentType: ac.developmentType,
      operatorNames: ac.operatorNames || [],
      subAreas: ac.subAreas?.map((s) => s.id).sort() || [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.state.values.areaConfig?.areaGeometryId,
    form.state.values.areaConfig?.developmentType,
    form.state.values.areaConfig?.operatorNames,
    form.state.values.areaConfig?.subAreas,
    scenarioId,
  ]);

  const baselineValidationKey = useMemo(() => {
    const ac = baselines.areaConfig;
    if (!ac?.areaGeometryId || !ac?.developmentType || !scenarioId) return null;
    return JSON.stringify({
      areaGeometryId: ac.areaGeometryId,
      developmentType: ac.developmentType,
      operatorNames: ac.operatorNames || [],
      subAreas: ac.subAreas?.map((s) => s.id).sort() || [],
    });
  }, [baselines.areaConfig, scenarioId]);

  useEffect(() => {
    if (!validationKey || !scenarioId) {
      setValidConfigState(null);
      return;
    }

    // Skip validation when form matches baselines (nothing changed to validate)
    if (validationKey === baselineValidationKey) {
      setValidConfigState(null);
      return;
    }

    const areaConfig = form.state.values.areaConfig!;
    const subAreas = areaConfig.subAreas?.map((s) => {
      if (areaConfig.subAreaCategory === 'type_curve_area') {
        return { typeCurveAreaId: Number(s.id) };
      }
      return { developmentAreaId: Number(s.id) };
    });

    const excludeAreaConfigId =
      selectedAreaConfig?.id && selectedAreaConfig.id !== 'local'
        ? Number(selectedAreaConfig.id)
        : undefined;

    setIsValidating(true);
    runValidateAreaConfig({
      payload: {
        typeCurveScenarioId: scenarioId,
        areaGeometryId: Number(areaConfig.areaGeometryId),
        developmentType: areaConfig.developmentType!.toLowerCase(),
        operatorNames: areaConfig.operatorNames?.length
          ? areaConfig.operatorNames
          : undefined,
        subAreas: subAreas?.length ? subAreas : undefined,
        excludeAreaConfigId,
      },
      onSuccess: (data) => {
        setValidConfigState({ isValid: data.valid, message: data.message });
        setIsValidating(false);
      },
      onError: (error) => {
        setValidConfigState({
          isValid: false,
          message: error || 'Validation failed',
        });
        setIsValidating(false);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationKey, baselineValidationKey]);

  return (
    <Accordion defaultExpanded>
      <AccordionSummary className={sectionStyles.accordionSummaryBorder}>
        <h3 className={sectionStyles.sectionTitle}>Area Configs</h3>
      </AccordionSummary>
      <AccordionDetails>
        <div className={sectionStyles.sectionDetails}>
          {/* Validation status */}
          {validateState.loading && (
            <InlineLoading
              className={styles.validationNotification}
              description="Validating area config..."
            />
          )}
          {!validateState.loading && validConfigState && (
            <InlineNotification
              className={styles.validationNotification}
              lowContrast
              kind={validConfigState.isValid ? 'success' : 'error'}
              // title={validConfigState.isValid ? 'Valid' : 'Invalid'}
              title={validConfigState.message}
              hideCloseButton
            />
          )}

          <p className={styles.description}>
            Area-specific configs for this geographic area and/or operators
            within the area.
          </p>

          {areaConfigDetail?.loading ? (
            <AreaConfigsSkeleton />
          ) : (
            <form.Subscribe selector={(state) => state.values.areaConfig}>
              {(areaConfig) => {
                if (!areaConfig) return null;

                return (
                  <div className={styles.inputsContainer}>
                    {/* Row 1: Basin Area Category */}
                    <DropdownField
                      id="basinAreaCategory"
                      label="Select category"
                      titleText={
                        <span className={styles.labelWithIcon}>
                          Basin Area Category (required)
                          {categoriesAtomValue.loading && (
                            <Loading
                              small
                              withOverlay={false}
                              description="Loading categories..."
                            />
                          )}
                        </span>
                      }
                      items={categoryItems}
                      itemToString={(item: string | null) => item ?? ''}
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                      disabled={
                        readOnly ||
                        categoriesAtomValue.loading ||
                        categoryItems.length === 1
                      }
                    />

                    {/* Row 1: Basin Area (ComboBox search) */}
                    <div>
                      <div
                        className={classNames(styles.comboBoxWrapper, {
                          [styles.shrinkComboBox]: selectedAreaGeometry,
                        })}
                      >
                        <Layer>
                          <ComboBox<AreaGeometryResult>
                            key={`basinArea-${selectedCategory}`}
                            id="basinArea"
                            placeholder="Search areas..."
                            titleText={
                              <span className={styles.labelWithIcon}>
                                Basin Area (required)
                                <Tooltip
                                  align="top"
                                  label="Basin Area defines the Formations available for the Area."
                                >
                                  <Information size={16} />
                                </Tooltip>
                                {basinAreaLoading && (
                                  <Loading
                                    small
                                    withOverlay={false}
                                    description="Loading areas..."
                                  />
                                )}
                              </span>
                            }
                            items={areaGeometries}
                            itemToString={(item) => item?.name ?? ''}
                            selectedItem={selectedAreaGeometry}
                            onChange={({ selectedItem: item }) => {
                              if (!item) return;
                              // Carbon also calls this when the saved basin
                              // area is set on load. That is not a new pick,
                              // so keep the basin name and sub areas.
                              if (String(item.id) === areaConfig.areaGeometryId)
                                return;

                              // Cancel any in-flight text search from typing
                              // before the user committed to this item, and
                              // arm the onInputChange guard so Carbon's echo
                              // of item.name doesn't dispatch a redundant
                              // search for the just-selected basin area.
                              debouncedTextSearch.cancel();
                              lastSelectedAreaNameRef.current = item.name;

                              setSelectedAreaGeometry(item);

                              const newAreaGeometryId = String(item.id);
                              const structureGridCollectionId =
                                item.structureGridCollectionId
                                  ? String(item.structureGridCollectionId)
                                  : null;

                              form.setFieldValue('areaConfig', {
                                ...areaConfig,
                                areaGeometryId: newAreaGeometryId,
                                areaGeometryName: item.name,
                                structureGridCollectionId,
                                structureGridCollectionName: null,
                                // Basin name only comes from structure grid collection API;
                                // clear it here — it will be populated when collection loads
                                basinName: null,
                                basinAreaCategory:
                                  item.category as BasinAreaCategory,
                                subAreas: [],
                              });

                              // Swap the basin area reference on the selected
                              // config immediately so the old polygon is
                              // removed from the map and the form/map stay in
                              // sync with the ComboBox. geoJson stays null
                              // until GET /area_geometries/:id returns; the
                              // detail-load effect below then fills it in and
                              // the map zooms via the selectedAreaGeoJson dep.
                              if (selectedAreaConfig?.id) {
                                const targetId = selectedAreaConfig.id;
                                setAreaConfigs((prev) =>
                                  prev.map((config) => {
                                    if (config.id !== targetId) return config;
                                    return {
                                      ...config,
                                      areaGeometry: {
                                        id: String(item.id),
                                        name: item.name,
                                        href: item.href,
                                        auth: [],
                                        geoJson: null,
                                      },
                                    };
                                  })
                                );
                              }

                              // Reset dependent fields on basin area change
                              setSubAreaOptions([]);
                              setOperatorOptions([]);
                              setSubAreaGeometries({});
                              setStructureGridCollectionData(null);
                              fetchedSubAreaIdsRef.current.clear();

                              // Signal formations.tsx to re-evaluate and create
                              // sentinel or clear based on current areaConfig state
                              setFormationsTrigger((prev) => prev + 1);

                              // Re-fetch sub areas if a sub area category is already selected
                              if (areaConfig.subAreaCategory) {
                                handleFetchSubAreas(
                                  areaConfig.subAreaCategory,
                                  newAreaGeometryId
                                );
                              }
                            }}
                            onInputChange={(text) => {
                              // Carbon mirrors a selection back into the
                              // input, which fires onInputChange with the
                              // selected item's name. Skip that one event so
                              // we don't issue a redundant search whose
                              // `text` equals the just-selected basin area.
                              if (
                                text &&
                                text === lastSelectedAreaNameRef.current
                              ) {
                                lastSelectedAreaNameRef.current = null;
                                return;
                              }
                              debouncedTextSearch(text ?? '');
                            }}
                            disabled={
                              readOnly || !selectedCategory || basinAreaLoading
                            }
                          />
                        </Layer>

                        {selectedAreaGeometry?.href && (
                          <div className={styles.launchButtonWrapper}>
                            <LaunchLink
                              to={selectedAreaGeometry.href}
                              className={styles.launchIcon}
                              label={`Open area geometry for ${selectedAreaGeometry.name} in a new tab`}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Row 1: Basin Area Name (populated from structure grid collection only) */}
                    <form.Field name={'areaConfig.basinName' as any}>
                      {(field) => (
                        <TanstackFieldWrapper<string | null> field={field}>
                          {(props) => (
                            <TextField
                              id="basinName"
                              labelText="Basin Area Name"
                              placeholder="Basin area name"
                              disabled={readOnly || !selectedAreaGeometry}
                              {...props}
                              value={props.value ?? ''}
                            />
                          )}
                        </TanstackFieldWrapper>
                      )}
                    </form.Field>

                    {/* Row 1: Sub Area Category */}
                    <form.Field name={'areaConfig.subAreaCategory' as any}>
                      {(field) => (
                        <TanstackFieldWrapper<SubAreaCategory | null>
                          field={field}
                        >
                          {(props) => (
                            <DropdownField
                              id="subAreaCategory"
                              label="Select category"
                              titleText="Sub Area Category (optional)"
                              items={SUB_AREA_CATEGORIES}
                              itemToString={(item: string | null) =>
                                categoryLabel(item)
                              }
                              disabled={readOnly || !areaConfig.areaGeometryId}
                              {...props}
                              onChange={(value) => {
                                props.onChange(value as SubAreaCategory | null);
                                form.setFieldValue('areaConfig', {
                                  ...form.state.values.areaConfig!,
                                  subAreas: [],
                                });
                                setSubAreaOptions([]);
                                setSubAreaGeometries({});
                                fetchedSubAreaIdsRef.current.clear();
                                if (value) {
                                  handleFetchSubAreas(
                                    value as SubAreaCategory,
                                    areaConfig.areaGeometryId
                                  );
                                }
                              }}
                            />
                          )}
                        </TanstackFieldWrapper>
                      )}
                    </form.Field>

                    {/* Row 2: Sub Area */}
                    <form.Field name={'areaConfig.subAreas' as any}>
                      {(field) => (
                        <TanstackFieldWrapper<SubArea[]> field={field}>
                          {(props) => {
                            const selectedIds = new Set(
                              (props.value || []).map((v) => v.id)
                            );
                            const selectedItems = subAreaOptions.filter((o) =>
                              selectedIds.has(o.id)
                            );

                            return (
                              <Layer>
                                <FilterableMultiSelect
                                  key={`subAreas-${subAreaOptions
                                    .map((o) => o.id)
                                    .join(',')}`}
                                  id="subAreas"
                                  titleText={
                                    <span className={styles.labelWithIcon}>
                                      Sub Area (optional)
                                      <Tooltip
                                        align="top"
                                        label="Sub Area defines geographic area where Configs apply. If none is defined Basin Area is used."
                                      >
                                        <Information size={16} />
                                      </Tooltip>
                                      {intersectShapesState.loading && (
                                        <Loading
                                          small
                                          withOverlay={false}
                                          description="Loading sub areas..."
                                        />
                                      )}
                                    </span>
                                  }
                                  placeholder="Select areas..."
                                  items={subAreaOptions}
                                  itemToString={(item: SubArea) =>
                                    item?.name ?? ''
                                  }
                                  initialSelectedItems={selectedItems}
                                  onChange={({ selectedItems }) => {
                                    props.onChange(selectedItems);
                                    handleSubAreaSelection(selectedItems);
                                  }}
                                  disabled={
                                    readOnly ||
                                    !areaConfig.subAreaCategory ||
                                    intersectShapesState.loading
                                  }
                                />
                              </Layer>
                            );
                          }}
                        </TanstackFieldWrapper>
                      )}
                    </form.Field>

                    {/* Row 2: Development Type */}
                    <form.Field name={'areaConfig.developmentType' as any}>
                      {(field) => (
                        <TanstackFieldWrapper<DevelopmentType | null>
                          field={field}
                        >
                          {(props) => (
                            <DropdownField
                              id="developmentType"
                              label="Select type"
                              titleText={
                                <span className={styles.labelWithIcon}>
                                  Development Type
                                  <Tooltip
                                    align="top"
                                    label="Specifies the Well Development Type the configs apply to."
                                  >
                                    <Information size={16} />
                                  </Tooltip>
                                </span>
                              }
                              items={DEVELOPMENT_TYPES}
                              itemToString={(item: string | null) => item ?? ''}
                              disabled={readOnly}
                              {...props}
                              onChange={(value) => {
                                props.onChange(value as DevelopmentType | null);

                                // Re-fetch operators with new development type
                                if (
                                  areaGeometryDetail?.data &&
                                  !('error' in areaGeometryDetail.data) &&
                                  areaGeometryDetail.data.geometry
                                ) {
                                  operatorsFetchedForRef.current = '';
                                  handleFetchOperators(
                                    areaGeometryDetail.data.geometry,
                                    value as DevelopmentType | null
                                  );
                                }
                              }}
                            />
                          )}
                        </TanstackFieldWrapper>
                      )}
                    </form.Field>

                    {/* Row 2: Operators */}
                    <form.Field name={'areaConfig.operatorNames' as any}>
                      {(field) => (
                        <TanstackFieldWrapper<string[]> field={field}>
                          {(props) => (
                            <Layer>
                              <FilterableMultiSelect
                                id="operatorNames"
                                titleText={
                                  <span className={styles.labelWithIcon}>
                                    Operators (optional)
                                    {fetchOperatorsState.loading && (
                                      <Loading
                                        small
                                        withOverlay={false}
                                        description="Loading operators..."
                                      />
                                    )}
                                  </span>
                                }
                                placeholder="Select operators..."
                                items={operatorOptions}
                                itemToString={(item: string) => item}
                                initialSelectedItems={props.value || []}
                                onChange={({ selectedItems }) => {
                                  props.onChange(selectedItems);
                                }}
                                disabled={
                                  readOnly ||
                                  !selectedAreaGeometry ||
                                  fetchOperatorsState.loading
                                }
                              />
                            </Layer>
                          )}
                        </TanstackFieldWrapper>
                      )}
                    </form.Field>
                  </div>
                );
              }}
            </form.Subscribe>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
